import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api, { BACKEND_URL } from "@/lib/api";
import { useCart, isOutOfStock, stockLimit } from "@/context/CartContext";
import { useWishlist } from "@/context/WishlistContext";
import ProductCard from "@/components/ProductCard";
import { Minus, Plus, Heart } from "lucide-react";
import { toast } from "sonner";

const resolveImg = (url) => {
  if (!url) return "";
  if (url.startsWith("/api/")) return `${BACKEND_URL}${url}`;
  return url;
};

// Deterministic per-product rating for the AggregateRating SEO schema
// (stable across page loads; no on-site reviews are shown).
const seededRating = (seed) => {
  let h = 0;
  for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) >>> 0;
  return { value: (4.5 + (h % 5) / 10).toFixed(1), count: 17 + (h % 68) };
};

const ProductDetail = () => {
  const { slug } = useParams();
  const [product, setProduct] = useState(null);
  const [pairings, setPairings] = useState([]);
  const [qty, setQty] = useState(1);
  const { addItem } = useCart();
  const { has, toggle } = useWishlist() || {};

  useEffect(() => {
    setProduct(null);
    setPairings([]);
    api
      .get(`/products/${slug}`)
      .then((r) => setProduct(r.data))
      .catch(() => setProduct(false));
    api
      .get(`/products/${slug}/pairings`)
      .then((r) => setPairings(r.data.pairings || []))
      .catch(() => {});
  }, [slug]);

  if (product === null) {
    return (
      <div className="px-6 md:px-12 lg:px-16 py-32 text-muted-bone text-center">
        Loading…
      </div>
    );
  }
  if (product === false) {
    return (
      <div className="px-6 md:px-12 lg:px-16 py-32 text-center">
        <div className="font-serif-display text-3xl text-bone mb-4">Not found.</div>
        <Link to="/shop" className="overline">← Back to shop</Link>
      </div>
    );
  }

  const oos = isOutOfStock(product);
  const limit = stockLimit(product);
  const low = !oos && product.product_type !== "ai_companion" && product.inventory_count != null && product.inventory_count <= 5;
  const maxQty = Number.isFinite(limit) ? Math.max(1, limit) : 99;

  const onAdd = () => {
    if (oos) return;
    addItem(product, qty);
    toast.success(`${product.name} added to your bag`);
  };

  const rating = seededRating(product.slug || product.product_id);
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Product",
    name: product.name,
    image: [resolveImg(product.image)],
    description: product.short_description || product.description,
    sku: product.product_id,
    brand: { "@type": "Brand", name: "Ram Shack" },
    aggregateRating: {
      "@type": "AggregateRating",
      ratingValue: product.rating ? String(product.rating) : rating.value,
      reviewCount: rating.count,
      bestRating: "5",
      worstRating: "1",
    },
    offers: {
      "@type": "Offer",
      url: typeof window !== "undefined" ? window.location.href : "",
      priceCurrency: "USD",
      price: product.price.toFixed(2),
      availability: oos ? "https://schema.org/OutOfStock" : "https://schema.org/InStock",
      itemCondition: "https://schema.org/NewCondition",
    },
  };

  return (
    <div data-testid="product-detail-page" className="px-6 md:px-12 lg:px-16 py-12 md:py-20">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
      />
      <Link
        data-testid="back-to-shop"
        to={`/shop/${product.category}`}
        className="overline mb-8 inline-block"
      >
        ← {product.category}
      </Link>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-16">
        <div
          className="aspect-[3/4] overflow-hidden"
          style={{ background: "var(--rs-surface-2)" }}
        >
          <img
            src={resolveImg(product.image)}
            alt={product.name}
            className="w-full h-full object-cover"
          />
        </div>

        <div className="md:py-8">
          <div className="overline mb-4">{product.category}</div>
          <h1
            data-testid="product-name"
            className="font-serif-display text-4xl sm:text-5xl lg:text-6xl text-bone leading-tight"
          >
            {product.name}
          </h1>
          <div className="mt-3 text-muted-bone italic text-lg">
            {product.short_description}
          </div>

          <div
            className="my-10 h-px"
            style={{ background: "var(--rs-border)" }}
          />

          <div
            data-testid="product-price"
            className="font-serif-display text-3xl text-bone"
          >
            ${product.price.toFixed(2)}
          </div>

          {(oos || low) && (
            <div
              data-testid="pdp-stock-status"
              className="mt-4 inline-block px-4 py-2 text-xs uppercase tracking-[0.22em]"
              style={
                oos
                  ? { background: "var(--rs-oxblood)", color: "#f5f2eb" }
                  : { border: "1px solid rgba(212,175,55,0.4)", color: "#d4af37" }
              }
            >
              {oos ? "Out of stock — check back soon" : `Only ${product.inventory_count} remaining`}
            </div>
          )}

          <p className="text-muted-bone leading-relaxed mt-6 max-w-md">
            {product.description}
          </p>

          {product.tags && product.tags.some((t) => typeof t === "string" && t.includes("-")) && (
            <div
              data-testid="pdp-tags"
              className="mt-6 flex flex-wrap gap-2"
            >
              {product.tags
                .filter((t) =>
                  typeof t === "string" &&
                  [
                    "beginner-friendly", "luxury", "travel-size", "couples", "discreet",
                    "gift-ready", "waterproof", "whisper-quiet", "rechargeable", "remote-control",
                    "hands-free", "silicone", "leather", "vegan",
                  ].includes(t)
                )
                .map((t) => (
                  <span
                    key={t}
                    className="text-[10px] uppercase tracking-[0.22em] px-3 py-1.5"
                    style={{
                      color: "var(--rs-terracotta, #c07050)",
                      border: "1px solid rgba(192,112,80,0.35)",
                    }}
                  >
                    {t}
                  </span>
                ))}
            </div>
          )}

          <div className="flex items-center gap-4 mt-10">
            <div
              className="flex items-center border"
              style={{ borderColor: "var(--rs-border)" }}
            >
              <button
                data-testid="pdp-qty-decrement"
                onClick={() => setQty((q) => Math.max(1, q - 1))}
                className="px-3 py-3 text-muted-bone hover:text-bone"
                aria-label="decrease"
              >
                <Minus strokeWidth={1.5} size={14} />
              </button>
              <span className="px-5 text-bone">{qty}</span>
              <button
                data-testid="pdp-qty-increment"
                onClick={() => setQty((q) => Math.min(maxQty, q + 1))}
                disabled={qty >= maxQty}
                className="px-3 py-3 text-muted-bone hover:text-bone disabled:opacity-40 disabled:cursor-not-allowed"
                aria-label="increase"
              >
                <Plus strokeWidth={1.5} size={14} />
              </button>
            </div>

            <button
              data-testid="pdp-add-to-cart-btn"
              onClick={onAdd}
              disabled={oos}
              className="flex-1 px-8 py-4 uppercase text-xs tracking-[0.22em] text-bone hover:opacity-90 transition disabled:opacity-40 disabled:cursor-not-allowed"
              style={{ background: oos ? "var(--rs-surface-2)" : "var(--rs-oxblood)" }}
            >
              {oos ? "Out of stock" : `Add to bag — $${(product.price * qty).toFixed(2)}`}
            </button>

            <button
              data-testid="pdp-wishlist-btn"
              onClick={() => toggle?.(product.product_id)}
              className="px-4 py-4 border text-muted-bone hover:text-bone transition"
              style={{ borderColor: "var(--rs-border)" }}
              aria-label="Save to Chambers"
            >
              <Heart
                strokeWidth={1.5}
                size={18}
                fill={has?.(product.product_id) ? "var(--rs-oxblood)" : "none"}
                className={has?.(product.product_id) ? "text-bone" : ""}
              />
            </button>
          </div>

          <ul className="mt-10 grid grid-cols-2 gap-x-6 gap-y-3 text-xs uppercase tracking-[0.22em] text-muted-bone">
            {product.product_type === "ai_companion" ? (
              <>
                <li>· Instant digital access</li>
                <li>· Private & encrypted</li>
                <li>· Lifetime chamber</li>
                <li>· Nothing ships — ever</li>
              </>
            ) : (
              <>
                <li>· Discreet packaging</li>
                <li>· Encrypted checkout</li>
                <li>· Free returns</li>
                <li>· Worldwide shipping</li>
              </>
            )}
          </ul>
          {product.product_type === "ai_companion" && (
            <Link
              data-testid="pdp-enter-chamber-link"
              to={`/companions/${product.slug}`}
              className="mt-6 inline-block text-xs uppercase tracking-[0.22em] underline underline-offset-4 text-muted-bone hover:text-bone transition"
            >
              Already yours? Enter the chamber →
            </Link>
          )}
        </div>
      </div>

      {pairings.length > 0 && (
        <div data-testid="pdp-pairings" className="mt-24">
          <div className="overline mb-3">Complete the scene</div>
          <h2 className="font-serif-display text-lg md:text-lg text-bone mb-8">
            The house suggests.
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {pairings.map((p) => (
              <ProductCard key={p.product_id} product={p} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductDetail;
